/**
 * #467 Particle Physics Engine (粒子物理引擎)
 *
 * 来源：2026 创意动画趋势 - 物理模拟动画
 * 类型：粒子系统 + 物理引擎 + 碰撞检测
 *
 * 技术要点：
 * 1. 牛顿力学模拟（F = ma）
 * 2. 粒子碰撞检测（圆形碰撞）
 * 3. 弹性碰撞响应（动量守恒）
 * 4. 重力场（向下加速度）
 * 5. 边界反弹（弹性系数 0.8）
 * 6. 粒子轨迹（最多 20 个历史点）
 * 7. Spring 物理入场动画
 * 8. 确定性随机算法（帧级一致性）
 *
 * 视觉效果：
 * - 深黑背景 + 渐变光晕
 * - 15 个发光粒子（HSL 颜色渐变）
 * - 粒子轨迹（半透明拖尾）
 * - 碰撞闪烁效果
 * - 背景网格装饰
 *
 * 适用场景：
 * - 物理教育视频
 * - 科技品牌展示
 * - 游戏开发教程
 * - 科学可视化
 * - 创意动画
 */

import React, { useMemo } from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  random,
} from 'remotion';

// ============ 类型定义 ============

interface Vector2D {
  x: number;
  y: number;
}

interface Particle {
  id: number;
  position: Vector2D;
  velocity: Vector2D;
  mass: number;
  radius: number;
  color: string;
  trail: Vector2D[];
  collisionFlash: number; // 碰撞闪烁计时器
}

interface PhysicsConfig {
  gravity: number; // 重力加速度
  restitution: number; // 弹性系数
  friction: number; // 摩擦系数
  maxTrailLength: number;
}

// ============ 物理引擎 ============

class PhysicsEngine {
  private config: PhysicsConfig;
  private width: number;
  private height: number;

  constructor(width: number, height: number, config: PhysicsConfig) {
    this.width = width;
    this.height = height;
    this.config = config;
  }

  // 更新粒子状态
  update(particle: Particle, deltaTime: number): void {
    // 1. 应用重力（F = ma, a = gravity）
    particle.velocity.y += this.config.gravity * deltaTime;

    // 2. 更新位置
    particle.position.x += particle.velocity.x * deltaTime;
    particle.position.y += particle.velocity.y * deltaTime;

    // 3. 边界碰撞检测
    this.handleBoundaryCollision(particle);

    // 4. 更新轨迹
    particle.trail.push({ ...particle.position });
    if (particle.trail.length > this.config.maxTrailLength) {
      particle.trail.shift();
    }

    // 5. 更新碰撞闪烁
    if (particle.collisionFlash > 0) {
      particle.collisionFlash -= deltaTime * 2;
    }
  }

  // 边界碰撞检测
  private handleBoundaryCollision(particle: Particle): void {
    const { radius, position, velocity } = particle;
    const { restitution } = this.config;

    // 左边界
    if (position.x - radius < 0) {
      position.x = radius;
      velocity.x *= -restitution;
      particle.collisionFlash = 1;
    }

    // 右边界
    if (position.x + radius > this.width) {
      position.x = this.width - radius;
      velocity.x *= -restitution;
      particle.collisionFlash = 1;
    }

    // 上边界
    if (position.y - radius < 0) {
      position.y = radius;
      velocity.y *= -restitution;
      particle.collisionFlash = 1;
    }

    // 下边界
    if (position.y + radius > this.height) {
      position.y = this.height - radius;
      velocity.y *= -restitution;
      particle.collisionFlash = 1;
    }
  }

  // 粒子碰撞检测（圆形碰撞）
  checkCollision(p1: Particle, p2: Particle): boolean {
    const dx = p2.position.x - p1.position.x;
    const dy = p2.position.y - p1.position.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance < p1.radius + p2.radius;
  }

  // 弹性碰撞响应（动量守恒）
  resolveCollision(p1: Particle, p2: Particle): void {
    const dx = p2.position.x - p1.position.x;
    const dy = p2.position.y - p1.position.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance === 0) return;

    // 法向量
    const nx = dx / distance;
    const ny = dy / distance;

    // 相对速度
    const dvx = p1.velocity.x - p2.velocity.x;
    const dvy = p1.velocity.y - p2.velocity.y;

    // 相对速度在法向量上的分量
    const dvn = dvx * nx + dvy * ny;

    // 如果粒子正在分离，不处理
    if (dvn > 0) return;

    // 计算冲量
    const { restitution } = this.config;
    const impulse = (-(1 + restitution) * dvn) / (1 / p1.mass + 1 / p2.mass);

    // 应用冲量
    p1.velocity.x += (impulse / p1.mass) * nx;
    p1.velocity.y += (impulse / p1.mass) * ny;
    p2.velocity.x -= (impulse / p2.mass) * nx;
    p2.velocity.y -= (impulse / p2.mass) * ny;

    // 分离粒子（避免重叠）
    const overlap = (p1.radius + p2.radius - distance) / 2;
    p1.position.x -= overlap * nx;
    p1.position.y -= overlap * ny;
    p2.position.x += overlap * nx;
    p2.position.y += overlap * ny;

    // 触发碰撞闪烁
    p1.collisionFlash = 1;
    p2.collisionFlash = 1;
  }
}

// ============ 确定性随机数生成器 ============

class SeededRandom {
  private seed: number;

  constructor(seed: number) {
    this.seed = seed;
  }

  next(): number {
    this.seed = (this.seed * 1103515245 + 12345) & 0x7fffffff;
    return this.seed / 0x7fffffff;
  }

  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }
}

// ============ 主组件 ============

export const ParticlePhysicsEngine: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  // 物理配置
  const physicsConfig: PhysicsConfig = useMemo(
    () => ({
      gravity: 500, // 重力加速度（像素/秒²）
      restitution: 0.85, // 弹性系数
      friction: 0.99, // 摩擦系数
      maxTrailLength: 20,
    }),
    []
  );

  // 初始化粒子（确定性）
  const particles: Particle[] = useMemo(() => {
    const rng = new SeededRandom(42);
    const result: Particle[] = [];

    for (let i = 0; i < 15; i++) {
      result.push({
        id: i,
        position: {
          x: rng.range(100, width - 100),
          y: rng.range(100, height / 2),
        },
        velocity: {
          x: rng.range(-200, 200),
          y: rng.range(-100, 100),
        },
        mass: rng.range(1, 3),
        radius: rng.range(15, 35),
        color: `hsl(${rng.range(0, 360)}, 70%, 60%)`,
        trail: [],
        collisionFlash: 0,
      });
    }

    return result;
  }, [width, height]);

  // 物理引擎实例
  const engine = useMemo(
    () => new PhysicsEngine(width, height, physicsConfig),
    [width, height, physicsConfig]
  );

  // 模拟物理（确定性）
  const simulatedParticles = useMemo(() => {
    const deltaTime = 1 / fps;
    const particlesCopy = particles.map((p) => ({
      ...p,
      position: { ...p.position },
      velocity: { ...p.velocity },
      trail: [...p.trail],
    }));

    // 逐帧模拟
    for (let f = 0; f <= frame; f++) {
      // 更新每个粒子
      particlesCopy.forEach((particle) => {
        engine.update(particle, deltaTime);
      });

      // 碰撞检测（两两检测）
      for (let i = 0; i < particlesCopy.length; i++) {
        for (let j = i + 1; j < particlesCopy.length; j++) {
          if (engine.checkCollision(particlesCopy[i], particlesCopy[j])) {
            engine.resolveCollision(particlesCopy[i], particlesCopy[j]);
          }
        }
      }
    }

    return particlesCopy;
  }, [frame, particles, engine, fps]);

  // Spring 入场动画
  const entranceProgress = spring({
    frame,
    fps,
    config: {
      damping: 200,
      stiffness: 100,
    },
  });

  const scale = interpolate(entranceProgress, [0, 1], [0, 1]);

  // 背景渐变动画
  const bgHue = (frame * 0.5) % 360;

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(135deg,
          hsl(${bgHue}, 30%, 8%) 0%,
          hsl(${(bgHue + 30) % 360}, 30%, 12%) 100%)`,
      }}
    >
      {/* 背景网格 */}
      <svg
        width={width}
        height={height}
        style={{
          position: 'absolute',
          opacity: 0.15,
          transform: `rotate(${frame * 0.2}deg) scale(1.5)`,
        }}
      >
        <defs>
          <pattern
            id="grid"
            width="40"
            height="40"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 40 0 L 0 0 0 40"
              fill="none"
              stroke="rgba(255,255,255,0.3)"
              strokeWidth="1"
            />
          </pattern>
        </defs>
        <rect width={width} height={height} fill="url(#grid)" />
      </svg>

      {/* 粒子轨迹 */}
      {simulatedParticles.map((particle) => (
        <svg
          key={`trail-${particle.id}`}
          width={width}
          height={height}
          style={{ position: 'absolute' }}
        >
          {particle.trail.length > 1 && (
            <path
              d={`M ${particle.trail[0].x} ${particle.trail[0].y} 
                  ${particle.trail
                    .map((p) => `L ${p.x} ${p.y}`)
                    .join(' ')}`}
              stroke={particle.color}
              strokeWidth={particle.radius * 0.3}
              fill="none"
              opacity={0.3}
              strokeLinecap="round"
            />
          )}
        </svg>
      ))}

      {/* 粒子 */}
      {simulatedParticles.map((particle, index) => {
        // 碰撞闪烁效果
        const flashIntensity = Math.max(0, particle.collisionFlash);
        const flashColor = `rgba(255, 255, 255, ${flashIntensity * 0.8})`;

        return (
          <div
            key={particle.id}
            style={{
              position: 'absolute',
              left: particle.position.x - particle.radius,
              top: particle.position.y - particle.radius,
              width: particle.radius * 2,
              height: particle.radius * 2,
              transform: `scale(${scale})`,
              transformOrigin: 'center',
            }}
          >
            {/* 外发光 */}
            <div
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                background: particle.color,
                filter: `blur(${10 + flashIntensity * 20}px)`,
                opacity: 0.6 + flashIntensity * 0.4,
              }}
            />
            {/* 主体 */}
            <div
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                background: `radial-gradient(circle at 30% 30%,
                  rgba(255,255,255,0.8),
                  ${particle.color} 50%,
                  rgba(0,0,0,0.5))`,
                boxShadow: `0 0 ${20 + flashIntensity * 30}px ${particle.color},
                            inset 0 0 ${10 + flashIntensity * 10}px ${flashColor}`,
              }}
            />
            {/* 碰撞闪烁 */}
            {flashIntensity > 0 && (
              <div
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  background: `radial-gradient(circle, ${flashColor}, transparent)`,
                }}
              />
            )}
          </div>
        );
      })}

      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: 40,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: entranceProgress,
          transform: `translateY(${interpolate(entranceProgress, [0, 1], [-30, 0])}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 48,
            fontWeight: 'bold',
            color: '#fff',
            fontFamily: 'Arial, sans-serif',
            margin: 0,
            textShadow: '0 0 20px rgba(255,255,255,0.5)',
          }}
        >
          粒子物理引擎
        </h1>
        <p
          style={{
            fontSize: 28,
            color: 'rgba(255,255,255,0.7)',
            fontFamily: 'Arial, sans-serif',
            marginTop: 8,
          }}
        >
          牛顿力学 · 弹性碰撞 · 动量守恒
        </p>
      </div>

      {/* 物理参数面板 */}
      <div
        style={{
          position: 'absolute',
          bottom: 40,
          left: 40,
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(10px)',
          borderRadius: 12,
          padding: '16px 24px',
          opacity: entranceProgress,
          transform: `translateX(${interpolate(entranceProgress, [0, 1], [-30, 0])}px)`,
        }}
      >
        <div style={{ color: '#fff', fontSize: 22, marginBottom: 8 }}>
          <span style={{ color: 'rgba(255,255,255,0.6)' }}>重力加速度：</span>
          <span style={{ color: '#4ade80', marginLeft: 8 }}>
            {physicsConfig.gravity} px/s²
          </span>
        </div>
        <div style={{ color: '#fff', fontSize: 22, marginBottom: 8 }}>
          <span style={{ color: 'rgba(255,255,255,0.6)' }}>弹性系数：</span>
          <span style={{ color: '#60a5fa', marginLeft: 8 }}>
            {physicsConfig.restitution}
          </span>
        </div>
        <div style={{ color: '#fff', fontSize: 22, marginBottom: 8 }}>
          <span style={{ color: 'rgba(255,255,255,0.6)' }}>粒子数量：</span>
          <span style={{ color: '#f472b6', marginLeft: 8 }}>
            {particles.length}
          </span>
        </div>
        <div style={{ color: '#fff', fontSize: 22 }}>
          <span style={{ color: 'rgba(255,255,255,0.6)' }}>当前帧：</span>
          <span style={{ color: '#fbbf24', marginLeft: 8 }}>{frame}</span>
        </div>
      </div>

      {/* 算法说明面板 */}
      <div
        style={{
          position: 'absolute',
          bottom: 40,
          right: 40,
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(10px)',
          borderRadius: 12,
          padding: '16px 24px',
          opacity: entranceProgress,
          transform: `translateX(${interpolate(entranceProgress, [0, 1], [30, 0])}px)`,
        }}
      >
        <div style={{ color: '#fff', fontSize: 22, marginBottom: 8 }}>
          <span style={{ color: 'rgba(255,255,255,0.6)' }}>碰撞检测：</span>
          <span style={{ color: '#4ade80', marginLeft: 8 }}>圆形碰撞</span>
        </div>
        <div style={{ color: '#fff', fontSize: 22, marginBottom: 8 }}>
          <span style={{ color: 'rgba(255,255,255,0.6)' }}>碰撞响应：</span>
          <span style={{ color: '#60a5fa', marginLeft: 8 }}>动量守恒</span>
        </div>
        <div style={{ color: '#fff', fontSize: 22, marginBottom: 8 }}>
          <span style={{ color: 'rgba(255,255,255,0.6)' }}>时间积分：</span>
          <span style={{ color: '#f472b6', marginLeft: 8 }}>Euler 方法</span>
        </div>
        <div style={{ color: '#fff', fontSize: 22 }}>
          <span style={{ color: 'rgba(255,255,255,0.6)' }}>确定性：</span>
          <span style={{ color: '#fbbf24', marginLeft: 8 }}>Seeded RNG</span>
        </div>
      </div>
    </AbsoluteFill>
  );
};
